<template lang='pug'>
v-dialog(v-model="showDialog" width="400px")
  v-card
    v-card-title.text-h6 {{ dialogTitle }}
    v-card-text
      div(v-if='!successMessage')
        p.text-body-1(v-if="isHtmlDescription" v-html="dialogDescription")
        p.text-body-1(v-else) {{ dialogDescription }}

      v-text-field(
        v-model='name'
        autofocus
        :placeholder="mailingListResource.name"
        :disabled="disableInput"
        filled
        color="accent"
      )
      v-text-field(
        v-model='emailAddress'
        ref='emailInput'
        :placeholder="mailingListResource.email"
        :disabled="disableInput"
        type="email"
        filled
        :error-messages="emailErrorMessages"
        color="accent"
        @keyup.enter='submitInfo'
      )

      p.text-body-1.success--text(v-if='!!successMessage') {{ successMessage }}
    v-card-actions
      v-spacer
      v-btn(color="accent" @click="closeDialog") {{ commonResource.close }}
      v-btn(color="accent" @click="submitInfo" :disabled='disableInput') {{ commonResource.submit }}
</template>

<script>
import Vue from 'vue'
import { getAuth } from 'firebase/auth'
import commonResource from '@/resources/common'
import mailingListResource from '@/resources/mailingList'
import accountResource from '@/resources/account'
import emailUtil from '@/util/emailUtil'
import day6Api from '@/api/day6'
import accountApi from '@/api/account'
import { quickytoolsAnalytics } from '@/util/analyticsUtil'

export default {
  name: 'MailingListAdd',
  props: {
    show: {
      type: Boolean,
      default: false
    },
    listKey: String,
    title: String,
    description: String,
    isHtmlDescription: Boolean
  },
  model: {
    prop: 'show',
    event: 'change'
  },
  data: function () {
    return {
      commonResource,
      mailingListResource,
      showDialog: false,
      name: '',
      emailAddress: '',
      isSending: false,
      successMessage: '',
      emailErrorMessages: []
    }
  },
  computed: {
    account () {
      return getAuth().currentUser
    },
    disableInput () {
      return this.isSending || !!this.successMessage
    },
    dialogTitle () {
      return this.title ? this.title : mailingListResource.mailingListTitle
    },
    dialogDescription () {
      return this.description ? this.description : mailingListResource.addToMailingList
    }
  },
  watch: {
    show (val) {
      this.showDialog = val
    },
    showDialog (val) {
      if (val) {
        const user = this.account
        if (user) {
          if (!this.emailAddress) {
            this.emailAddress = user.email
          }
          if (!this.name) {
            const emailName = user.email ? user.email.split('@', 1)[0] : ''
            const names = [user.displayName, emailName].filter(s => s)
            this.name = names.length ? names[0] : ''
          }
        }
      } else {
        this.closeDialog()
      }
    }
  },
  methods: {
    closeDialog () {
      this.resetState()
      this.$emit('change', false)
    },
    resetState () {
      this.name = ''
      this.emailAddress = ''
      this.successMessage = ''
    },

    validateInput () {
      let errorMessage = ''
      let focusInput = null

      if (!this.emailAddress) {
        errorMessage = mailingListResource.emailRequired
        focusInput = this.$refs.emailInput
      } else if (!emailUtil.isValidEmailAddress(this.emailAddress)) {
        errorMessage = mailingListResource.invalidEmailAddress
        focusInput = this.$refs.emailInput
      }

      if (errorMessage) {
        this.emailErrorMessages = [errorMessage]
        if (focusInput) {
          focusInput.$el.querySelector('input').focus()
        }
      }

      return errorMessage ? {} : { name: this.name, email: this.emailAddress }
    },

    submitInfo () {
      const contactInfo = this.validateInput()

      if (contactInfo.email) {
        this.emailErrorMessages = []
      } else {
        return
      }

      this.addToMailingList(contactInfo)
    },

    async addToMailingList (contactInfo) {
      let apiCall
      let successMessage
      switch (this.listKey) {
        case 'day6-marketing':
          apiCall = day6Api.addToMarketingList
          break
        case 'free-trial-signup':
          apiCall = accountApi.addToFreeTrialSignup
          successMessage = accountResource.freeTrialStarted
          break
        case 'pay-interest-inquiry':
          // This shows for both free trials and paid subscriptions (20220318)
          apiCall = accountApi.addToPayInterestList
          successMessage = mailingListResource.beInTouch
          break
      }

      if (apiCall) {
        this.isSending = true
        try {
          await apiCall(contactInfo)
          this.successMessage = successMessage || mailingListResource.addedToMailingList
          this.$emit('info-submit')
        } catch (e) {
          // TODO Error message and handle closing
          Vue.logger.error(e)
        } finally {
          this.isSending = false
        }

        quickytoolsAnalytics.submitMailingList(this.listKey)
      } else {
        Vue.logger.log(`Mailing list ${this.listKey} not supported`)
      }

      const delayMs = this.successMessage ? 3000 : 0
      setTimeout(() => this.closeDialog(), delayMs)
    }
  }
}
</script>
